import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { isEmpty } from 'lodash';
import { defer, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HighlightFeedback } from '../../../team-apis/otterdam/feedback';
import { UpsertFieldFeedbackOperationAttributes } from '../../../team-apis/otterdam/feedback-operations/field-feedback-operations';
import { PutShapeFeedbackDtoAttributes } from '../../../team-apis/otterdam/feedback-operations/shape-feedback-operations';
import { SamplingWorkspaceStore } from '../../sampling-workspace/state/sampling-workspace-store';
import { PersistenceService } from '../../shared/misc-services/persistence.service';
import {
  FeedbackOperation,
  FeedbackResource,
} from './feedback-resource.service';

const feedbackQueueName = 'Feedback';

@Injectable()
export class FeedbackPersistenceService {
  private persistenceService = inject(PersistenceService);
  private pendingOperations: FeedbackOperation[] = [];
  private feedbackResource = inject(FeedbackResource);
  private samplingWorkspaceStore = inject(SamplingWorkspaceStore);

  upsertFieldFeedback$(
    outputId: number,
    attributes: UpsertFieldFeedbackOperationAttributes,
  ) {
    return this.queueOperations$({
      operation: 'UPSERT_FIELD_FEEDBACK',
      outputId,
      attributes,
    });
  }

  upsertHighlight$(highlightFeedback: HighlightFeedback) {
    return this.queueOperations$({
      operation: 'UPSERT_OBJECT_FEEDBACK',
      objectId: highlightFeedback.objectId,
      feedbackId: highlightFeedback.feedbackId,
      attributes: {
        itemSubType: 'highlight',
        tagName: 'text',
        sampleId: this.samplingWorkspaceStore.state().sample.id,
        stepId: highlightFeedback.stepId,
        errorTag: highlightFeedback.errorTag,
        comment: highlightFeedback.comment,
        startIndex: highlightFeedback.startIndex,
        endIndex: highlightFeedback.endIndex,
        wordCount: highlightFeedback.wordCount,
      },
    });
  }

  upsertShapeFeedback$(
    outputId: number,
    feedbackId: string,
    attributes: PutShapeFeedbackDtoAttributes,
  ) {
    return this.queueOperations$({
      operation: 'UPSERT_SHAPE_FEEDBACK',
      outputId,
      feedbackId,
      attributes,
    });
  }

  deleteFieldFeedback$(outputId: number) {
    return this.queueOperations$({
      operation: 'DELETE_FIELD_FEEDBACK',
      outputId,
    });
  }

  deleteHighlight$(highlightFeedback: HighlightFeedback) {
    return this.queueOperations$({
      operation: 'DELETE_OBJECT_FEEDBACK',
      tagName: 'text',
      objectId: highlightFeedback.objectId,
      feedbackId: highlightFeedback.feedbackId,
    });
  }

  deleteShapeFeedback$(outputId: number, feedbackId: string, shapeId?: number) {
    return this.queueOperations$({
      operation: 'DELETE_SHAPE_FEEDBACK',
      outputId,
      ...(isDefined(shapeId) ? { shapeId } : {}),
      feedbackId,
    });
  }

  private queueOperations$(...operations: FeedbackOperation[]) {
    this.pendingOperations.push(...operations);

    return this.persistenceService.enqueue$(
      defer(() => {
        if (isEmpty(this.pendingOperations)) {
          return of(null);
        }

        const operationsToUpload = this.pendingOperations;
        this.pendingOperations = [];

        return this.feedbackResource.sendOperations$(operationsToUpload).pipe(
          tap({
            error: () => {
              // on error, we re-add the uploaded operations to the pending
              // list so they can be picked up on retry
              this.pendingOperations.unshift(...operationsToUpload);
            },
          }),
        );
      }),
      feedbackQueueName,
    );
  }
}
